import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { Api, Code } from '@.api';
import { StationResource } from '@/entities/Station';
import { createSelectors } from '@/Lib';
import { ConsoleDev } from '@/Lib/ConsoleDev';
import { Chamber, IRomaStationWS, SaveStationDeviceLicenseKeyFail, SaveStationDeviceLicenseKeySuccess } from '@/Services/IRomaStationWS';
import { ChambersResponse } from '@/Services/IRomaStationWS/ChambersResponse';

import { StationTechnicalModeEnum } from './StationTechnicalModeEnum';

interface StationDetailState {
    iRomaStationWS: IRomaStationWS | null;

    isLoadingChangeStationMode: boolean;

    station: StationResource | null;

    stationTechnicalMode: StationTechnicalModeEnum;
}

interface StationDetailActions {
    setStation: (station: StationResource) => void;

    onTechnicalMode: () => void;
    offTechnicalMode: () => void;

    loadStation: (stationId: number) => void;
    deleteStation: () => void;
    restoreStation: () => void;

    connectToStationWS: (userAccessToken: string, station: StationResource) => Promise<IRomaStationWS>;
    disconnectFromStationWS: () => Promise<void>;
    startEditChambers: (deviceId: number) => Promise<ChambersResponse>;
    saveChambers: (deviceId: number, chambers: Chamber[]) => Promise<number>;
    saveNewLicenseKeyForDevice: (deviceId: number, licenseKey: string) => Promise<SaveStationDeviceLicenseKeySuccess | SaveStationDeviceLicenseKeyFail>;

    setIsLoadingChangeStationMode: (value: boolean) => void;

    resetState: () => void;
}

type StationDetailStore = StationDetailState & StationDetailActions;

const createInitialStationDetailState = (): StationDetailState => {
    return {
        iRomaStationWS: null,
        isLoadingChangeStationMode: false,
        station: null,
        stationTechnicalMode: StationTechnicalModeEnum.OFF,
    };
};

export const stationDetailStore = create<StationDetailStore>()(
    immer<StationDetailStore>((set, getState, store) => ({
        ...createInitialStationDetailState(),
        resetState: createInitialStationDetailState,
        setStation: (station: StationResource) => {
            set((state) => {
                state.station = station;
            });
        },
        onTechnicalMode: () => {
            set((state) => {
                state.stationTechnicalMode = StationTechnicalModeEnum.ON;
            });
        },
        offTechnicalMode: () => {
            set((state) => {
                state.stationTechnicalMode = StationTechnicalModeEnum.OFF;
            });
        },
        setIsLoadingChangeStationMode: (value: boolean) => {
            set((state) => {
                state.isLoadingChangeStationMode = value;
            });
        },
        loadStation: (stationId: number) => {
            const state = getState();

            return Api.stations()
                .getById(stationId)
                .onSuccess((res) => state.setStation(res.data));
        },
        deleteStation: () => {
            const state = getState();

            if (!state.station?.id) {
                return;
            }

            const stationId = state.station.id;

            Api.stations()
                .restore(stationId)
                .then((res) => {
                    if (res?.code === Code.Success) {
                        Api.stations()
                            .getById(stationId)
                            .onSuccess((res) => state.setStation(res.data));
                    }
                });
        },
        restoreStation: () => {
            const state = getState();

            if (!state.station?.id) {
                return;
            }

            const stationId = state.station.id;

            Api.stations()
                .delete(stationId)
                .then((res) => {
                    if (res?.code === Code.Success) {
                        Api.stations()
                            .getById(stationId)
                            .onSuccess((res) => state.setStation(res.data));
                    }
                });
        },
        connectToStationWS: async (userAccessToken: string, station: StationResource) => {
            const state: StationDetailStore = getState();

            if (state.iRomaStationWS) {
                throw new Error('IRomaStationWS instance already exists.');
            }

            state.setIsLoadingChangeStationMode(true);

            const iRomaStationWS = await IRomaStationWS.createInstance(userAccessToken);

            try {
                await iRomaStationWS.joinToStation(station.id);
            } catch (error) {
                state.setIsLoadingChangeStationMode(false);

                ConsoleDev.error('[connectToStationWs]: ', error);

                throw error;
            }

            set((state) => {
                state.iRomaStationWS = iRomaStationWS;
            });

            state.onTechnicalMode();

            state.setIsLoadingChangeStationMode(false);

            iRomaStationWS.onStationDisconnected(() => {
                state.disconnectFromStationWS();
            });

            return iRomaStationWS;
        },
        disconnectFromStationWS: async () => {
            const state = getState();

            state.setIsLoadingChangeStationMode(true);

            if (state.iRomaStationWS) {
                await state.iRomaStationWS.destroyInstance();

                set((state) => {
                    state.iRomaStationWS = null;
                });
            }

            state.offTechnicalMode();

            state.setIsLoadingChangeStationMode(false);

            state.resetState();
        },
        startEditChambers: async (deviceId: number): Promise<ChambersResponse> => {
            const state = getState();

            if (!state.iRomaStationWS) {
                throw new Error('Need connection to station.');
            }

            if (!state.station) {
                throw new Error('station required');
            }

            const chambers = await state.iRomaStationWS.sendChambersStartEdit(state.station.id, deviceId);

            return chambers;
        },
        saveChambers: async (deviceId, chambers) => {
            const state = getState();

            if (!state.station?.id) {
                throw new Error('station id required');
            }

            if (!state.iRomaStationWS) {
                throw new Error('For save need station connection');
            }

            return await state.iRomaStationWS.sendChambersSave(state.station.id, deviceId, chambers);
        },
        saveNewLicenseKeyForDevice: async (
            deviceId: number,
            licenseKey: string
        ): Promise<SaveStationDeviceLicenseKeySuccess | SaveStationDeviceLicenseKeyFail> => {
            const state = getState();

            if (!state.station?.id) {
                throw new Error('station id required');
            }

            if (!state.iRomaStationWS) {
                throw new Error('For save need station connection');
            }

            return await state.iRomaStationWS.saveNewLicenseKeyForDevice(state.station.id, deviceId, licenseKey);
        },
    }))
);

export const stationDetailStoreSelectors = createSelectors(stationDetailStore);
